import { useEffect, useState } from 'react'
import { App, Card, Col, Row, Space, Statistic, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  downloadMaintenanceReportCsv,
  maintenanceReportsApi,
  type ReportFilters,
} from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ReportFilterBar from './components/ReportFilterBar'
import { useMasterLookups } from './components/useMasterLookups'

const { Text } = Typography

interface Row {
  transaction_id: string
  transaction_no: string
  transaction_date: string | null
  spare_id: string
  spare_code: string | null
  spare_name: string | null
  category: string | null
  uom: string | null
  machine_id: string | null
  department_id: string | null
  work_order_no: string | null
  issued_qty: number
  returned_qty: number
  net_consumed_qty: number
  rate: number
  value: number
  current_stock: number | null
  reorder_level: number | null
  critical_spare: boolean
}

/**
 * Spec §7 — Spare consumption report.
 */
export default function SpareConsumptionReportPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const { machineCode, deptName } = useMasterLookups()
  const [filters, setFilters] = useState<ReportFilters>({
    plant_id: plantId,
    from_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    to_date: dayjs().format('YYYY-MM-DD'),
  })
  useEffect(() => {
    if (filters.plant_id !== plantId) setFilters((f) => ({ ...f, plant_id: plantId }))
  }, [plantId, filters.plant_id])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['report-spare-consumption', filters],
    enabled: !!filters.plant_id,
    queryFn: () => maintenanceReportsApi.spareConsumption(filters) as unknown as Promise<Row[]>,
  })
  const rows = data ?? []
  const totalValue = rows.reduce((a, r) => a + (r.value ?? 0), 0)
  const totalNet = rows.reduce((a, r) => a + (r.net_consumed_qty ?? 0), 0)
  const lowStock = rows.filter((r) =>
    r.current_stock != null && r.reorder_level != null && r.current_stock <= r.reorder_level,
  ).length

  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      await downloadMaintenanceReportCsv(
        '/maintenance/reports/spare-consumption', filters,
        `spare-consumption-${dayjs().format('YYYYMMDD-HHmm')}.csv`,
      )
    } catch (e) { message.error((e as Error).message ?? 'Export failed') }
    finally { setExporting(false) }
  }

  const columns = [
    { title: 'Txn No', dataIndex: 'transaction_no', key: 't', width: 130 },
    { title: 'Date', dataIndex: 'transaction_date', key: 'd', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM HH:mm') : '—',
    },
    { title: 'Spare', dataIndex: 'spare_code', key: 'sc', width: 130 },
    { title: 'Name', dataIndex: 'spare_name', key: 'sn', ellipsis: true },
    { title: 'Cat', dataIndex: 'category', key: 'c', width: 110 },
    {
      title: 'Machine', key: 'm', width: 140,
      render: (_: unknown, r: Row) => machineCode(r.machine_id),
    },
    {
      title: 'Dept', key: 'dt', width: 140,
      render: (_: unknown, r: Row) => deptName(r.department_id),
    },
    { title: 'WO', dataIndex: 'work_order_no', key: 'wo', width: 130 },
    { title: 'Issued', dataIndex: 'issued_qty', key: 'iq', width: 90 },
    { title: 'Returned', dataIndex: 'returned_qty', key: 'rq', width: 100 },
    { title: 'Net', dataIndex: 'net_consumed_qty', key: 'nq', width: 90 },
    { title: 'UOM', dataIndex: 'uom', key: 'u', width: 70 },
    { title: 'Rate', dataIndex: 'rate', key: 'r', width: 100,
      render: (v: number) => `₹ ${v.toFixed(2)}` },
    { title: 'Value', dataIndex: 'value', key: 'v', width: 110,
      render: (v: number) => `₹ ${v.toFixed(2)}` },
    {
      title: 'Stock', key: 'stk', width: 130,
      render: (_: unknown, r: Row) => {
        if (r.current_stock == null) return '—'
        const low = r.reorder_level != null && r.current_stock <= r.reorder_level
        return (
          <Space>
            <Text strong style={{ color: low ? '#cf1322' : undefined }}>{r.current_stock}</Text>
            {low && <Tag color="red">REORDER</Tag>}
          </Space>
        )
      },
    },
    {
      title: 'Critical', dataIndex: 'critical_spare', key: 'cs', width: 100,
      render: (v: boolean) => v ? <Tag color="orange">CRITICAL</Tag> : null,
    },
  ]

  return (
    <div>
      <PageHeader title="Spare Consumption"
        subtitle="Issued, returned, net consumed quantity per spare against work orders." />
      <ReportFilterBar
        filters={filters} onChange={setFilters} onRefresh={() => refetch()}
        onExport={exportCsv} exporting={exporting}
        show={{ dateRange: true, department: true, machine: true, criticalOnly: true }}
      />
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="Issue transactions" value={rows.length} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Net consumed qty" value={totalNet} precision={2} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Total value" value={totalValue} prefix="₹" precision={2} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Spares at reorder" value={lowStock} valueStyle={{ color: lowStock > 0 ? '#faad14' : undefined }} /></Card></Col>
      </Row>
      <DataTable rowKey="transaction_id" columns={columns} dataSource={rows} loading={isLoading} />
    </div>
  )
}
